// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
// import PlusCircleFill from "@store/Components/Toolkit/Icons/PlusCircle/PlusCircleFill"; // Store Custom Code
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { useIsPunchOutSession } from "@store/Framework/Store/Context/ContextHooks";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector"; // Store Custom Code
import getColor from "@insite/mobius/utilities/getColor";
import { getCompleteProductListIds } from "@store/Framework/Common/Utilities/productToStorageList";
import HeartFill from "@store/Components/Toolkit/Icons/Heart/HeartFill";
import HeartLine from "@store/Components/Toolkit/Icons/Heart/HeartLine";
import HeartPlus from "@store/Components/Toolkit/Icons/Heart/HeartPlus";
import { linkGetIconOnlyLinkProps } from "@store/Themes/common/props/link";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";
/* Store Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
    // Store Custom Code
    overridedStyles?: ProductAddToListLinkStyles;
    showTitle?: boolean;
    showIcon?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface ProductAddToListLinkStyles {
    link?: LinkPresentationProps;
    // Store Custom Code
    addToListIconLink?: LinkPresentationProps;
    alreadyInListIconLink?: LinkPresentationProps;
}

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {
    link: linkGetActionLinkProps(HeartPlus), // Store Custom Code
    /* Store Custom Code - Start */
    addToListIconLink: mergeProps<ProductAddToListLinkStyles["link"]>(linkGetIconOnlyLinkProps(HeartLine, 30), {
        css: css`
            &:hover {
                color: ${getColor("text.link")};
            }
        `,
    }),
    alreadyInListIconLink: mergeProps<ProductAddToListLinkStyles["link"]>(linkGetIconOnlyLinkProps(HeartFill, 30), {
        css: css`
            color: ${getColor("tertiary.dark")};

            &:hover {
                color: ${getColor("text.link")};
            }
        `,
    }),
    /* Store Custom Code - End */
};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    /* Store Custom Code - Start */
    overridedStyles,
    showTitle,
    showIcon,
    /* Store Custom Code - End */
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const styles = useExpandProps(productAddToListLinkStyles, extendedStyles, overridedStyles); // Store Custom Code
    const isPunchOutSession = useIsPunchOutSession(); // Starter Custom Code
    /* Store Custom Code - Start */
    const [refresh, setRefresh] = React.useState(false);
    /* Store Custom Code - End */

    if (!product.canAddToWishlist || isPunchOutSession /* Starter Custom Code */) {
        return null;
    }

    /* Store Custom Code - Start */
    let wishlistLinkStyles = styles.link;
    let labelContainedInLists = "";

    if (showIcon) {
        const productListIds = getCompleteProductListIds(product);

        if (productListIds.length) {
            wishlistLinkStyles = styles.alreadyInListIconLink;
            labelContainedInLists = translate("Contained in {0} wishlist(s)", `${productListIds.length}`);
        } else {
            wishlistLinkStyles = styles.addToListIconLink;
        }
    }
    /* Store Custom Code - End */

    const addToListLinkClickHandler = () => {
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                    setRefresh(!refresh); // Store Custom Code
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const label = labelContainedInLists !== "" ? labelContainedInLists : (labelOverride ?? translate("Add to List")); // Store Custom Code

    return (
        <Link
            {...wishlistLinkStyles} // Store Custom Code
            onClick={addToListLinkClickHandler}
            disabled={productInfo.qtyOrdered <= 0}
            title={showTitle ? label : undefined} // Store Custom Code
            {...otherProps}
        >
            {label /* Store Custom Code */}
        </Link>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAddToListLink));
